import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Category as CategoryIcon,
  MedicalServices as ServicesIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  CreditCard as CreditCardIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SubscriptionStatistics, LeadStatistics, EventStatistics } from '../../types';
import AdminActivityLogPanel from '../../components/admin/AdminActivityLogPanel';

interface DashboardStats {
  totalCategories: number;
  totalServices: number;
  totalProfessionals: number;
  totalPatients: number;
  todayBookingPatients: number;
  pendingServiceVerifications: number;
  pendingProfessionalVerifications: number;
  subscriptionStats?: SubscriptionStatistics;
  leadStats?: LeadStatistics;
  eventStats?: EventStatistics;
}

const DashboardPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, hasAnyPermission, hasPermission, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalCategories: 0,
    totalServices: 0,
    totalProfessionals: 0,
    totalPatients: 0,
    todayBookingPatients: 0,
    pendingServiceVerifications: 0,
    pendingProfessionalVerifications: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const modulePermissions = [
    'admin.service_categories.manage',
    'admin.verification.manage',
    'admin.users.view',
    'admin.subscriptions.view',
    'admin.subscriptions.manage',
    'admin.leads.monitor',
    'admin.leads.analytics.view',
    'admin.contact_reveal.view',
    'admin.payments.view',
    'admin.payments.manage',
    'admin.activity_logs.view',
    'admin.admin_comments.manage',
    'admin.system_settings.manage',
    'admin.payment.settings.manage',
    'admin.role_management.view',
    'admin.user_roles.assign',
  ];
  const hasModuleAccess = hasAnyPermission(modulePermissions);

  useEffect(() => {
    if (!authLoading && isAuthenticated && hasModuleAccess) {
      fetchDashboardData();
    } else if (!authLoading && isAuthenticated && !hasModuleAccess) {
      setIsLoading(false);
    }
  }, [authLoading, isAuthenticated, hasModuleAccess]);

    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        setError('');

        const getLocalDateString = (d: Date) => {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        };
        const today = getLocalDateString(new Date());

        const canViewUsers = hasPermission('admin.users.view');
        const canManageCategories = hasPermission('admin.service_categories.manage');
        const canVerify = hasPermission('admin.verification.manage');
        const canViewSubscriptions = hasAnyPermission(['admin.subscriptions.view', 'admin.subscriptions.manage']);
        const canViewLeads = hasPermission('admin.leads.analytics.view');
        const canViewContactReveal = hasPermission('admin.contact_reveal.view');

        const [
          categoriesResponse,
          servicesResponse,
          pendingResponse,
          userStats,
          pendingProfessionalsResponse,
          todayBookingAnalytics,
          subscriptionStats,
          leadStats,
          eventStats,
        ] = await Promise.all([
          canManageCategories ? apiService.getServiceCategories().catch(() => null) : Promise.resolve(null),
          canVerify ? apiService.getProfessionalServices({ per_page: 1 }).catch(() => null) : Promise.resolve(null),
          canVerify ? apiService.getPendingVerificationServices().catch(() => null) : Promise.resolve(null),
          canViewUsers ? apiService.getUserStatistics().catch(() => null) : Promise.resolve(null),
          canVerify ? apiService.getUsers({ user_type: 'professional', is_verified: false, per_page: 1 }).catch(() => null) : Promise.resolve(null),
          canViewUsers ? apiService.getBookingAnalytics({ date_from: today, date_to: today }).catch(() => null) : Promise.resolve(null),
          canViewSubscriptions ? apiService.getSubscriptionStatistics().catch(() => null) : Promise.resolve(null),
          canViewLeads ? apiService.getLeadStatistics().catch(() => null) : Promise.resolve(null),
          canViewContactReveal ? apiService.getEventStatistics().catch(() => null) : Promise.resolve(null),
        ]);

        setStats({
          totalCategories: categoriesResponse?.length ?? 0,
          totalServices: servicesResponse?.pagination?.total ?? 0,
          totalProfessionals: userStats?.by_user_type?.professional ?? 0,
          totalPatients: userStats?.by_user_type?.patient ?? 0,
          todayBookingPatients: todayBookingAnalytics?.unique_patients ?? 0,
          pendingServiceVerifications: pendingResponse?.pagination?.total ?? 0,
          pendingProfessionalVerifications: pendingProfessionalsResponse?.pagination?.total ?? 0,
          subscriptionStats: subscriptionStats || undefined,
          leadStats: leadStats || undefined,
          eventStats: eventStats || undefined,
        });
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
          </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Please log in to view the dashboard.
        </Alert>
        </Box>
  );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!hasModuleAccess) {
    return (
      <Box p={3}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              No Accessible Modules
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Your account is active, but no admin modules are assigned to your role yet.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please contact the super administrator to assign at least one `admin.*` permission.
            </Typography>
            <Button
              variant="outlined"
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
            >
              Logout
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const statCards = [
    ...(hasPermission('admin.service_categories.manage') ? [{
      title: 'Total Categories',
      value: stats.totalCategories,
      icon: <CategoryIcon />,
      color: '#1976d2',
    }] : []),
    ...(hasPermission('admin.verification.manage') ? [{
      title: 'Total Services',
      value: stats.totalServices,
      icon: <ServicesIcon />,
      color: '#388e3c',
    }] : []),
    ...(hasPermission('admin.users.view') ? [{
      title: 'Total Professionals',
      value: stats.totalProfessionals,
      icon: <PeopleIcon />,
      color: '#f57c00',
    }] : []),
    ...(hasPermission('admin.verification.manage') ? [{
      title: 'Pending Service Verifications',
      value: stats.pendingServiceVerifications,
      icon: <TrendingUpIcon />,
      color: '#d32f2f',
    }, {
      title: 'Pending Professional Verifications',
      value: stats.pendingProfessionalVerifications,
      icon: <TrendingUpIcon />,
      color: '#c62828',
    }] : []),
    ...(hasPermission('admin.users.view') ? [{
      title: 'Total Patients',
      value: stats.totalPatients,
      icon: <PeopleIcon />,
      color: '#1565c0',
    }, {
      title: 'Today Booking Patients',
      value: stats.todayBookingPatients,
      icon: <TrendingUpIcon />,
      color: '#2e7d32',
    }] : []),
    ...(stats.subscriptionStats ? [{
      title: 'Active Subscriptions',
      value: stats.subscriptionStats.active_subscriptions,
      icon: <CreditCardIcon />,
      color: '#9c27b0',
    }, {
      title: 'Revenue This Month',
      value: `₹${stats.subscriptionStats.revenue_this_month.toFixed(2)}`,
      icon: <TrendingUpIcon />,
      color: '#4caf50',
    }] : []),
    ...(stats.leadStats ? [{
      title: 'Total Leads',
      value: stats.leadStats.total_leads,
      icon: <AssignmentIcon />,
      color: '#1976d2',
    }, {
      title: 'New Leads Today',
      value: stats.leadStats.new_leads_today ?? 0,
      icon: <TrendingUpIcon />,
      color: '#4caf50',
    }, {
      title: 'Conversion Rate (Won/Total)',
      value: `${(stats.leadStats.conversion_rate || 0).toFixed(1)}%`,
      icon: <TrendingUpIcon />,
      color: '#388e3c',
    }] : []),
    ...(stats.eventStats ? [{
      title: 'Events Today (Contact Reveals)',
      value: stats.eventStats.events_today ?? 0,
      icon: <VisibilityIcon />,
      color: '#9c27b0',
    }] : []),
  ];

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Welcome to Arogya Healthcare Admin Panel
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {statCards.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      {card.title}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {card.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: card.color,
                      borderRadius: '50%',
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {React.cloneElement(card.icon, { sx: { color: 'white' } })}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box mt={4}>
        <Typography variant="h5" gutterBottom>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
        {hasPermission('admin.service_categories.manage') && <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card 
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
              onClick={() => navigate('/categories')}
            >
              <CardContent>
                <Box display="flex" alignItems="center">
                  <CategoryIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Manage Categories</Typography>
                </Box>
              </CardContent>
            </Card>
        </Grid>}
        {hasPermission('admin.verification.manage') && <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card 
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
              onClick={() => navigate('/services')}
            >
              <CardContent>
                <Box display="flex" alignItems="center">
                  <ServicesIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Verify Services</Typography>
                </Box>
              </CardContent>
            </Card>
        </Grid>}
        {hasPermission('admin.users.view') && <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card 
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
              onClick={() => navigate('/professionals')}
            >
              <CardContent>
                <Box display="flex" alignItems="center">
                  <PeopleIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Manage Users</Typography>
                </Box>
              </CardContent>
            </Card>
        </Grid>}
        {hasPermission('admin.payments.view') && <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card 
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
              onClick={() => navigate('/payments/reports')}
            >
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">View Reports</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>}
        </Grid>
      </Box>
      {hasPermission('admin.activity_logs.view') && <AdminActivityLogPanel />}
    </Box>
  );
};

export default DashboardPage;